'use client'

import { useGetPropertyByIdQuery } from '@/stores/api/customer-api'
import {
  resolveSectionMatch,
  type SectionMatch
} from '@/utils/resolve-section-match'

import { useEffect, useMemo, useRef } from 'react'

import { usePathname } from 'next/navigation'

/**
 * Hook to dynamically set page titles for all apps.
 *
 * Uses the shared `resolveSectionMatch` walker to resolve the title from
 * the route's section config.
 *
 * Examples:
 * - Customer-level pages: "S5000 - Cases | Atlas"
 * - Portal property pages: "Hilton Garden Inn - Overview | Atlas"
 * - Org-level pages: "Opportunities | Atlas"
 *
 * To add or change a page title, update the relevant `*.config.ts`.
 * Pages with no config entry fall back to a formatted version of the
 * last URL segment.
 */
export function usePageTitle() {
  const pathname = usePathname()
  const timeoutRef = useRef<NodeJS.Timeout | null>(null)

  const match = useMemo(() => resolveSectionMatch(pathname), [pathname])

  // Portal `[id]` params are property UUIDs, not human-readable customer
  // numbers — resolve the property name for the title. The site header
  // already subscribes to this query for the breadcrumb label, so on portal
  // property pages this is a cache hit, not a second request.
  const isPortalProperty = match?.prefix === 'portal' && !!match.params.id
  const { data: portalProperty } = useGetPropertyByIdQuery(
    match?.params.id ?? '',
    { skip: !isPortalProperty }
  )
  const propertyName = isPortalProperty ? portalProperty?.name : undefined

  useEffect(() => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current)

    timeoutRef.current = setTimeout(() => {
      const title = resolvePageTitle(pathname, match, propertyName)
      const finalTitle = `${title} | Atlas`
      document.title = finalTitle

      // Restore title if overridden during development Fast Refresh
      setTimeout(() => {
        if (document.title !== finalTitle) document.title = finalTitle
      }, 500)
      setTimeout(() => {
        if (document.title !== finalTitle) document.title = finalTitle
      }, 1000)
    }, 100)

    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current)
    }
  }, [pathname, match, propertyName])
}

function resolvePageTitle(
  pathname: string,
  match: SectionMatch | null,
  propertyName?: string
): string {
  if (match) {
    // Customer-level pages have `[id]` extracted — prepend as identifier.
    if (match.params.id) {
      // Portal ids are property UUIDs — show the resolved name instead, or
      // just the section title while it loads (never flash the raw UUID).
      if (match.prefix === 'portal') {
        return propertyName ? `${propertyName} - ${match.title}` : match.title
      }
      return `${decodeURIComponent(match.params.id)} - ${match.title}`
    }
    return match.title
  }

  // Fallback: format the last meaningful path segment. An id or token
  // (accept-invitation/RK8Ixxw92DZ…) isn't meaningful: use the segment
  // before it, so the tab reads "Accept Invitation", not the invite id.
  const segments = pathname
    .split('/')
    .filter(Boolean)
    .map((s) => decodeURIComponent(s))
  const named = segments.filter((s) => !looksLikeId(s))
  const last = named[named.length - 1] ?? segments[segments.length - 1] ?? ''
  return last
    .split('-')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')
}

/** A generated id (nanoid, UUID): long and containing a digit. */
function looksLikeId(segment: string): boolean {
  return segment.length >= 12 && /\d/.test(segment)
}
